import { Project } from "$src/domain/projects/Project";
import { get, writable } from "svelte/store";
import * as jsonpatch from "fast-json-patch";
import { hubStore } from "$src/application/realtime/hubStore";
import { setEditActive } from "$src/application/main/mainTemplateStore";
import { ProjectDraft } from "$src/domain/projects/drafts/ProjectDraft";
import { DueDateType } from "$src/domain/projects/details/PaymentSchedule";
import { InstallationType } from "$src/domain/projects/installations/InstallationType";
import { RoadFromPoints } from "$src/application/map/commands/ComputeRoadFromPoints";
import { Road } from "$src/domain/map/Road";

export const projectGroupStoreId = writable<number>();
export const projectOriginal = writable<Project>();
export const projectDraft = writable<Project>();
export const projectDraftPatchNumber = writable<number>();
export const projectDraftObserver = writable<jsonpatch.Observer<Project>>();
export const projectFormErrors = writable<ProjectSectionError[]>([]);
export const projectInstallationAndDeliveryChanged = writable<number>(0);
export const projectGeoCodesChanged = writable<RoadFromPoints | null>(null);
export const roadStore = writable<Road | null>(null);

export interface ProjectSectionError {
  section: ProjectFormSection;
  errors: string[];
}

export enum ProjectFormSection {
  Info,
  InfoCustomer,
  Delivery,
  Amounts,
  Items,
}

export function startEditProjectDraft(
  groupStoreId: number,
  draftData: ProjectDraft
) {
  projectGroupStoreId.set(groupStoreId);
  projectDraftPatchNumber.set(draftData.patchNumber);
  projectDraft.set(draftData.draft);
  projectDraftObserver.set(jsonpatch.observe(draftData.draft));

  setEditActive(true);
  get(hubStore)?.startEditingProject(groupStoreId, draftData.projectId);
}

export function onTakeOverProjectDraft() {
  setEditActive(false);
}

export function publishProjectDraft() {}

export function clearProjectFormErrors() {
  projectFormErrors.set([]);
}

export function addProjectSectionError(sectionError: ProjectSectionError) {
  projectFormErrors.update((errors) => {
    const index = errors.findIndex((s) => s.section === sectionError.section);
    if (index === -1) {
      return [...errors, sectionError];
    } else {
      const updatedErrors = [...errors];
      updatedErrors[index] = sectionError;
      return updatedErrors;
    }
  });
}

export function hasProjectFormErrors(): boolean {
  return (
    get(projectFormErrors).find(
      (section) => section.errors != null && section.errors.length > 0
    ) != null
  );
}

export function hasProjectFormSectionErrors(
  section: ProjectFormSection
): boolean {
  return (
    get(projectFormErrors).find(
      (s) => s.section == section && s.errors != null && s.errors.length > 0
    ) != null
  );
}

export function registerErrors(
  section: ProjectFormSection,
  e: { [x: string]: string[] | null }
) {
  const allErrors: string[] = Object.entries(e)
    .filter(([key, val]) => key !== "contacts" && val !== null && val.length == 1)
    .map(([_, val]) => val)
    .flat()
    .filter((val) => val != null && val != undefined);

  addProjectSectionError({
    section: section,
    errors: allErrors,
  });
}

const p1Percent: number = 30;

export function createProjectSchedule(project: Project) {
  var amount1 = (project.prices.totalAmountIncVat / 100) * p1Percent;
  var amount2 = project.prices.totalAmountIncVat - amount1;

  if (!project?.schedule?.payments || project?.schedule?.payments.length == 0) {
    if (project.installation.type != InstallationType.None) {
      project.schedule = {
        payments: [
          {
            paymentNumber: 1,
            amount: amount1,
            autoDueDateType: DueDateType.AfterOrder,
            autoDueDateDays: 15,
            paymentCompleted: false,
            comments: "",
          },
          {
            paymentNumber: 2,
            amount: amount2,
            autoDueDateType: DueDateType.BeforeInstallation,
            autoDueDateDays: 15,
            paymentCompleted: false,
            comments: "",
          },
        ],
      };
    }
    else {
      project.schedule = {
        payments: [
          {
            paymentNumber: 1,
            amount: project.prices.totalAmountIncVat,
            autoDueDateType: DueDateType.AfterOrder,
            autoDueDateDays: 15,
            paymentCompleted: false,
            comments: "",
          },
        ],
      };
    }
  }
}
